// Match Chat API Service
// Handles all API calls for team-to-team match communication

import api from './api';

const matchChatAPI = {
    getRequestConfig: (teamId) => (
        teamId ? { params: { team_id: teamId } } : undefined
    ),

    getRequestBody: (payload, teamId) => (
        teamId ? { ...payload, team_id: teamId } : payload
    ),

    // Get all messages for a match
    getMessages: async (matchId, teamId) => {
        try {
            const response = await api.get(`/match-chat/${matchId}/messages`, matchChatAPI.getRequestConfig(teamId));
            return response;
        } catch (error) {
            console.error('Error fetching messages:', error);
            throw error;
        }
    },

    // Heartbeat while match chat is open (player JWT) — suppresses push for this match
    postViewing: async (matchId, teamId) => {
        try {
            const response = await api.post(
                `/match-chat/${matchId}/viewing`,
                matchChatAPI.getRequestBody({}, teamId)
            );
            return response;
        } catch (error) {
            return { success: false };
        }
    },

    // Send a new message
    sendMessage: async (matchId, message, teamId) => {
        try {
            const response = await api.post(
                `/match-chat/${matchId}/messages`,
                matchChatAPI.getRequestBody({ message }, teamId)
            );
            return response;
        } catch (error) {
            console.error('Error sending message:', error);
            throw error;
        }
    },

    // Mark messages as read
    markAsRead: async (matchId, teamId) => {
        try {
            const response = await api.post(
                `/match-chat/${matchId}/mark-read`,
                matchChatAPI.getRequestBody({}, teamId)
            );
            return response;
        } catch (error) {
            console.error('Error marking messages as read:', error);
            throw error;
        }
    },

    // Get unread message counts for all matches
    getUnreadCounts: async (teamId) => {
        try {
            const response = await api.get('/match-chat/unread-counts', matchChatAPI.getRequestConfig(teamId));
            return response;
        } catch (error) {
            console.error('Error fetching unread counts:', error);
            throw error;
        }
    },

    // Get match info
    getMatchInfo: async (matchId, teamId) => {
        try {
            const response = await api.get(`/match-chat/${matchId}/info`, matchChatAPI.getRequestConfig(teamId));
            return response;
        } catch (error) {
            console.error('Error fetching match info:', error);
            throw error;
        }
    },

    // Get phone number for a team if it has been revealed
    getPhoneNumber: async (matchId, teamId, requestingTeamId) => {
        try {
            const response = await api.get(
                `/match-chat/${matchId}/phone-number/${teamId}`,
                matchChatAPI.getRequestConfig(requestingTeamId)
            );
            return response;
        } catch (error) {
            console.error('Error fetching phone number:', error);
            throw error;
        }
    },

    // Update typing status. active false clears it. A newer seq wins over a late older request.
    updateTypingStatus: async (matchId, teamId, { active = true, seq = 0, signal } = {}) => {
        try {
            const response = await api.post(
                `/match-chat/${matchId}/typing`,
                matchChatAPI.getRequestBody({ active, seq }, teamId),
                signal ? { signal } : undefined
            );
            return response;
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                return { success: false };
            }
            console.debug('Typing status update failed (non-critical):', error);
            return { success: false };
        }
    }
};

export default matchChatAPI;

